//  Angular imports
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
//  Application imports
import { environment } from 'environments/environment';
//  Third party imports
import { catchError, first, map } from 'rxjs/operators';
import { GenericDeserialize } from 'cerialize';
import { Observable, of } from 'rxjs';

/* eslint-disable  @typescript-eslint/no-explicit-any */

@Injectable({
    providedIn: 'root'
})
export class BaseProvisioningService {
    private http = inject(HttpClient);

    public baseUrl: string = environment.provisioningApi;

    public apiPost<TRequest, TResponse>(apiPath: string,
        apiBody: TRequest, type: new () => TResponse = null!): Observable<TResponse> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        return this.http.post<TResponse>(url, apiBody)
            .pipe(
                first(),
                map(
                    (response: TResponse): TResponse => GenericDeserialize(response, type)
                )
            );
    }

    public apiPut<TRequest, TResponse>(apiPath: string, apiBody: TRequest, type: new () => TResponse = null!): Observable<TResponse> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        return this.http.put<TResponse>(url, apiBody)
            .pipe(
                first(),
                map(
                    (response: TResponse): TResponse => GenericDeserialize(response, type)
                )
            );
    }

    public get<T = any>(apiPath: string, urlParams: HttpParams | undefined = undefined): Observable<T> {
        const url = `${this.baseUrl}/${apiPath}`;
        return this.http.get<T>(url, { params: urlParams }).pipe(first());
    }

    public apiGet<T>(
        apiPath: string,
        type: new () => T = null!,
        observeResponse: boolean = false,
        urlParams: HttpParams | undefined = undefined
    ): Observable<HttpResponse<T | Array<T>> | T | Array<T>> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        // we want to observe the response, so we can't expect a string back (string !== HttpResponse<T>)
        if (observeResponse) {
            return this.http.get<T>(url, { observe: 'response', params: urlParams })
                .pipe(
                    first(),
                    map(
                        (response: HttpResponse<T>): HttpResponse<T | Array<T>> => {
                            if (response.headers.get('Content-Type') === 'application/json') {
                                const responseClone: HttpResponse<T | Array<T>> = new HttpResponse({
                                    body: GenericDeserialize(response.body, type),
                                    headers: response.headers,
                                    status: response.status,
                                    statusText: response.statusText,
                                    url: response.url ?? undefined
                                });
                                return responseClone;
                            }
                            return response;
                        }
                    )
                );
            // we don't care about the response but we need a typed object, so we pass in a truthy constructor as type
        } else if (type !== null) {
            return this.http.get<T>(url, { params: urlParams })
                .pipe(
                    first(),
                    map(
                        (response: T): T => GenericDeserialize(response, type)
                    )
                );
            // we passed in a null type so we expect a plain string
        } else {
            return this.http.get(url, { responseType: 'text', params: urlParams })
                .pipe(
                    first(),
                    map(
                        (response: string): T => GenericDeserialize(response, type)
                    )
                );
        }
    }

    public apiDelete(apiPath: string): Observable<boolean> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        return this.http.delete(url)
            .pipe(
                first(),
                map(() => true),
                catchError(
                    (error: unknown): Observable<boolean> => {

                        console.error(error);
                        return of(false);
                    }
                )
            );
    }

    public apiDeleteBody<TRequest, TResponse>(apiPath: string, apiBody: TRequest, type: new () => TResponse = null!): Observable<TResponse> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        return this.http.delete<TResponse>(url, {
            body: apiBody
        })
            .pipe(
                first(),
                map(
                    (response: TResponse): TResponse => GenericDeserialize(response, type)
                )
            );
    }

    public apiHead(apiPath: string): Observable<boolean> {
        const url: string = `${this.baseUrl}/${apiPath}`;
        return this.http.head(url)
            .pipe(
                first(),
                map(() => true),
                catchError((): Observable<boolean> => of(false))
            );
    }
}

@Injectable({ providedIn: 'root' })
export class FortiCloudBaseProvisioningService extends BaseProvisioningService {
    override baseUrl = environment.provisioningApi;
}

@Injectable({ providedIn: 'root' })
export class FortiFACBaseProvisioningService extends BaseProvisioningService {
    override baseUrl = environment.adminApi;
}
